// services/articlesApi.js
// ---------------------------------------------------------------------------
// Fetches doctor-published articles, videos, and white papers.
//
// Backend endpoints:
//   GET /api/articles?doctorId=<id>&type=<TYPE>        — list (no `body`)
//   GET /api/articles/{id}                              — single (HAS `body`, increments viewCount)
//   GET /api/articles/mine                               — current doctor's own content
//   Headers: X-Tenant-ID: <tenantId>
//
// List response shape (array) — note: `body` is intentionally omitted here,
// only the single-item GET /api/articles/{id} includes it:
//   [
//     {
//       id: "uuid",
//       doctorId: "uuid",
//       doctorName: "Dr. Ananya Sharma",
//       doctorPhoto: "https://...",
//       department: "Cardiology",
//       type: "ARTICLE" | "VIDEO" | "WHITEPAPER",
//       title: "Managing Hypertension in 2025",
//       summary: "A practical guide to...",
//       thumbnailUrl: "https://...",
//       readTimeMinutes: 5,          // ARTICLE / WHITEPAPER
//       videoDurationSecs: 420,      // VIDEO
//       videoUrl: "https://...",     // VIDEO — resolved from either an external link or an uploaded file
//       publishedDate: "2025-03-15",
//       tags: ["Cardiology", "Heart Health"],
//       viewCount: 1240,
//       externalUrl: "https://...",  // optional — link to full paper/source
//     },
//     ...
//   ]
//
// Single-item response (GET /api/articles/{id}) — same shape PLUS:
//       body: "Full markdown/plain-text content of the article…"
// ---------------------------------------------------------------------------

import { API_BASE_URL as BASE_URL } from './apiBaseUrl';

// ---------------------------------------------------------------------------
// MOCK DATA — remove once /api/articles is live
// ---------------------------------------------------------------------------
const MOCK_ARTICLES = [
  // ── Articles ──────────────────────────────────────────────────────────────
  {
    id: 'art-1',
    doctorId: 'doc-1',
    doctorName: 'Dr. Ananya Sharma',
    doctorPhoto: 'https://api.dicebear.com/7.x/personas/svg?seed=ananya',
    department: 'Cardiology',
    type: 'ARTICLE',
    title: 'Understanding Hypertension: The Silent Killer',
    summary:
      'High blood pressure often shows no symptoms until serious damage has occurred. This guide covers risk factors, lifestyle modifications, and when to seek medical attention.',
    body:
      'Hypertension is often called the "silent killer" because it rarely causes symptoms until it has already damaged the heart, kidneys, or blood vessels.\n\n' +
      'Risk factors include a family history of high blood pressure, a diet high in sodium, sedentary lifestyle, obesity, and chronic stress. Many of these are modifiable.\n\n' +
      'Lifestyle changes that help lower blood pressure include reducing salt intake, regular aerobic exercise (at least 150 minutes a week), maintaining a healthy weight, limiting alcohol, and managing stress through sleep and relaxation techniques.\n\n' +
      'If lifestyle changes are not enough, your doctor may prescribe medication such as ACE inhibitors, ARBs, calcium channel blockers, or diuretics. It is important to take these consistently and not stop without medical advice.\n\n' +
      'Seek medical attention immediately if you experience severe headache, chest pain, shortness of breath, or vision changes alongside high blood pressure readings — these can be signs of a hypertensive emergency.',
    thumbnailUrl: 'https://images.unsplash.com/photo-1530026405186-ed1f139313f8?w=600&q=80',
    readTimeMinutes: 6,
    publishedDate: '2025-04-10',
    tags: ['Cardiology', 'Heart Health', 'Hypertension'],
    viewCount: 2140,
    externalUrl: null,
  },
  {
    id: 'art-2',
    doctorId: 'doc-2',
    doctorName: 'Dr. Ravi Menon',
    doctorPhoto: 'https://api.dicebear.com/7.x/personas/svg?seed=ravi',
    department: 'Endocrinology',
    type: 'ARTICLE',
    title: 'Living Well with Type 2 Diabetes',
    summary:
      'Practical, evidence-based strategies for managing blood sugar through diet, exercise, and medication — written for patients and their families.',
    thumbnailUrl: 'https://images.unsplash.com/photo-1576086213369-97a306d36557?w=600&q=80',
    readTimeMinutes: 8,
    publishedDate: '2025-03-22',
    tags: ['Diabetes', 'Endocrinology', 'Nutrition'],
    viewCount: 3870,
    externalUrl: null,
  },
  {
    id: 'art-3',
    doctorId: 'doc-3',
    doctorName: 'Dr. Priya Nair',
    doctorPhoto: 'https://api.dicebear.com/7.x/personas/svg?seed=priya',
    department: 'Pediatrics',
    type: 'ARTICLE',
    title: "Your Child's Vaccination Schedule Explained",
    summary:
      'A month-by-month breakdown of the immunisation schedule recommended by the Indian Academy of Pediatrics, with FAQs for parents.',
    thumbnailUrl: 'https://images.unsplash.com/photo-1559757175-0eb30cd8c063?w=600&q=80',
    readTimeMinutes: 5,
    publishedDate: '2025-05-01',
    tags: ['Pediatrics', 'Vaccines', 'Child Health'],
    viewCount: 1560,
    externalUrl: null,
  },

  // ── Videos ────────────────────────────────────────────────────────────────
  {
    id: 'vid-1',
    doctorId: 'doc-1',
    doctorName: 'Dr. Ananya Sharma',
    doctorPhoto: 'https://api.dicebear.com/7.x/personas/svg?seed=ananya',
    department: 'Cardiology',
    type: 'VIDEO',
    title: 'How to Read Your ECG Report',
    summary:
      'In this 7-minute walkthrough, Dr. Sharma explains the key components of an ECG printout and what common findings mean for your heart health.',
    thumbnailUrl: 'https://images.unsplash.com/photo-1504813184591-01572f98c85f?w=600&q=80',
    videoDurationSecs: 427,
    publishedDate: '2025-02-14',
    tags: ['Cardiology', 'ECG', 'Diagnostics'],
    viewCount: 5210,
    externalUrl: 'https://www.youtube.com',
  },
  {
    id: 'vid-2',
    doctorId: 'doc-4',
    doctorName: 'Dr. Suresh Rao',
    doctorPhoto: 'https://api.dicebear.com/7.x/personas/svg?seed=suresh',
    department: 'Orthopaedics',
    type: 'VIDEO',
    title: '5 Exercises to Relieve Lower Back Pain',
    summary:
      'Dr. Rao demonstrates a daily stretching routine that has helped hundreds of his patients reduce chronic lower-back pain without surgery.',
    thumbnailUrl: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=600&q=80',
    videoDurationSecs: 312,
    publishedDate: '2025-04-28',
    tags: ['Orthopaedics', 'Back Pain', 'Exercise'],
    viewCount: 7830,
    externalUrl: 'https://www.youtube.com',
  },
  {
    id: 'vid-3',
    doctorId: 'doc-2',
    doctorName: 'Dr. Ravi Menon',
    doctorPhoto: 'https://api.dicebear.com/7.x/personas/svg?seed=ravi',
    department: 'Endocrinology',
    type: 'VIDEO',
    title: 'Intermittent Fasting & Insulin Resistance',
    summary:
      'A concise clinical overview of the evidence behind intermittent fasting protocols for patients with insulin resistance and pre-diabetes.',
    thumbnailUrl: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=600&q=80',
    videoDurationSecs: 558,
    publishedDate: '2025-01-30',
    tags: ['Diabetes', 'Nutrition', 'Fasting'],
    viewCount: 4190,
    externalUrl: 'https://www.youtube.com',
  },

  // ── White Papers ──────────────────────────────────────────────────────────
  {
    id: 'wp-1',
    doctorId: 'doc-1',
    doctorName: 'Dr. Ananya Sharma',
    doctorPhoto: 'https://api.dicebear.com/7.x/personas/svg?seed=ananya',
    department: 'Cardiology',
    type: 'WHITEPAPER',
    title: 'Statin Therapy in South Asian Populations: A Clinical Review',
    summary:
      'This white paper reviews 12 clinical trials examining statin efficacy and tolerability in South Asian patients, with adjusted dosing recommendations for Indian practitioners.',
    thumbnailUrl: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=600&q=80',
    readTimeMinutes: 18,
    publishedDate: '2024-11-05',
    tags: ['Cardiology', 'Statins', 'Clinical Review'],
    viewCount: 890,
    externalUrl: null,
  },
  {
    id: 'wp-2',
    doctorId: 'doc-2',
    doctorName: 'Dr. Ravi Menon',
    doctorPhoto: 'https://api.dicebear.com/7.x/personas/svg?seed=ravi',
    department: 'Endocrinology',
    type: 'WHITEPAPER',
    title: 'GLP-1 Receptor Agonists: Emerging Evidence for NAFLD',
    summary:
      'An evidence synthesis of recent RCTs evaluating the hepatoprotective effects of GLP-1 agonists in non-alcoholic fatty liver disease alongside T2DM management.',
    thumbnailUrl: 'https://images.unsplash.com/photo-1576671081837-49000212a370?w=600&q=80',
    readTimeMinutes: 22,
    publishedDate: '2024-12-18',
    tags: ['Endocrinology', 'NAFLD', 'GLP-1'],
    viewCount: 640,
    externalUrl: null,
  },
  {
    id: 'wp-3',
    doctorId: 'doc-3',
    doctorName: 'Dr. Priya Nair',
    doctorPhoto: 'https://api.dicebear.com/7.x/personas/svg?seed=priya',
    department: 'Pediatrics',
    type: 'WHITEPAPER',
    title: 'Antibiotic Stewardship in Paediatric Primary Care',
    summary:
      'Guidelines and decision frameworks for reducing unnecessary antibiotic prescriptions in children under 12, based on WHO and IAP recommendations.',
    thumbnailUrl: 'https://images.unsplash.com/photo-1631549916768-4119b2e5f926?w=600&q=80',
    readTimeMinutes: 15,
    publishedDate: '2025-02-07',
    tags: ['Pediatrics', 'Antibiotics', 'Stewardship'],
    viewCount: 1020,
    externalUrl: null,
  },
];
// ---------------------------------------------------------------------------

/**
 * Format video duration as "m:ss" or "h:mm:ss"
 */
export const formatDuration = (secs) => {
  if (!secs) return '';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
};

/**
 * Fetch articles/videos/whitepapers.
 * @param {string|null} tenantId
 * @param {string|null} doctorId  — pass null to fetch for all doctors (Home page)
 * @param {'ARTICLE'|'VIDEO'|'WHITEPAPER'|null} type — pass null for all types
 */
export const getArticles = async (tenantId, doctorId = null, type = null) => {
  // Once the backend is ready, swap the catch/fallback lines below.
  try {
    const params = new URLSearchParams();
    if (doctorId) params.set('doctorId', doctorId);
    if (type)     params.set('type', type);

    const res = await fetch(`${BASE_URL}/articles?${params}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
      },
    });

    if (!res.ok) return _applyFilters(MOCK_ARTICLES, doctorId, type); // ← remove mock fallback when live
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return _applyFilters(MOCK_ARTICLES, doctorId, type);               // ← remove mock fallback when live
  }
};

// Internal helper — remove once backend is live
const _applyFilters = (items, doctorId, type) => {
  let result = items;
  if (doctorId) result = result.filter(a => a.doctorId === doctorId);
  if (type)     result = result.filter(a => a.type === type);
  return result;
};

/**
 * Fetch a single article/video/whitepaper by id — hits GET /api/articles/{id}.
 * This is the ONLY call that returns the full `body` field and increments
 * the server-side view count, so the detail page must use this instead of
 * filtering the list endpoint's response.
 * @param {string} id
 * @param {string|null} tenantId
 */
export const getArticleById = async (id, tenantId = null) => {
  try {
    const res = await fetch(`${BASE_URL}/articles/${id}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
      },
    });

    if (!res.ok) {
      if (res.status === 404) return null;
      // ← remove mock fallback when backend is fully live everywhere
      return MOCK_ARTICLES.find(a => a.id === id) || null;
    }
    return await res.json();
  } catch {
    return MOCK_ARTICLES.find(a => a.id === id) || null; // ← remove mock fallback when live
  }
};

/**
 * Fetch the CURRENT doctor's own articles/videos/whitepapers for the
 * dashboard "My Content" tab. The doctor is resolved server-side from the
 * JWT — no doctorId is passed, so there's no risk of guessing the wrong id
 * (or falling back to placeholder mock content) when the auth context
 * hasn't fully loaded yet.
 * @param {string} token
 * @param {'ARTICLE'|'VIDEO'|'WHITEPAPER'|null} type — pass null for all types
 */
export const getMyArticles = async (token, tenant, type = null) => {
  const params = new URLSearchParams();
  if (type) params.set('type', type);

  const res = await fetch(`${BASE_URL}/articles/mine?${params}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(tenant?.id ? { 'X-Tenant-ID': tenant.id } : {}),
    },
  });

  if (!res.ok) {
    throw new Error('Failed to load your content');
  }
  const data = await res.json();
  return Array.isArray(data) ? data : [];
};